'use client';

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Spinner } from '@phosphor-icons/react';
import { authApi } from '../services/api';
import { teamApi } from '../api/team';
import { useAuthStore } from '../stores/authStore';
import { useTeamStore } from '../stores/teamStore';
import { Button } from '../components/ui/Button';
import { Footer } from './components/Footer';

export function GoogleCallbackPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { setCurrentTeamId } = useTeamStore();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState(t('auth.googleProcessing'));

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // 检查是否有错误
    if (error) {
      setStatus('error');
      setMessage(errorDescription || t('auth.googleAuthFailed'));
      return;
    }

    // 检查必要参数
    if (!code) {
      setStatus('error');
      setMessage(t('auth.missingCode'));
      return;
    }

    // 验证 state（可选的安全检查）
    const savedState = sessionStorage.getItem('google_state');
    if (savedState && savedState !== state) {
      setStatus('error');
      setMessage(t('auth.securityCheckFailed'));
      return;
    }

    // 清除保存的 state
    sessionStorage.removeItem('google_state');

    // 调用后端完成登录
    handleGoogleLogin(code, state || '');
  }, [searchParams, t]);

  const handleGoogleLogin = async (code: string, state: string) => {
    try {
      const response = await authApi.googleCallback(code, state);
      
      if (response.code !== 0 || !response.data) {
        throw new Error(response.message || t('auth.loginFailed'));
      }

      const { user, tokens } = response.data;

      // 使用返回的 token 登录
      login(
        {
          id: user.id,
          username: user.username,
          email: user.email,
          nickname: user.nickname,
          role: user.role,
          avatar: user.avatar,
        },
        tokens.access_token
      );

      // 获取用户的团队列表，设置当前团队
      try {
        const teamsResponse = await teamApi.listTeams();
        const teams = teamsResponse.data?.teams ?? [];
        if (teamsResponse.code === 0 && teams.length > 0) {
          setCurrentTeamId(teamsResponse.data?.currentTeamId ?? teams[0].id);
        } else {
          setCurrentTeamId("");
        }
      } catch (err) {
        setCurrentTeamId("");
      }

      setStatus('success');
      setMessage(t('auth.loginSuccessRedirect'));

      // 延迟跳转到首页
      setTimeout(() => {
        navigate('/home', { replace: true });
      }, 1500);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.loginFailed');
      setStatus('error');
      setMessage(errorMsg);
    }
  };

  const handleRetry = () => {
    navigate('/login', { replace: true });
  };

  const handleGoHome = () => {
    navigate('/home', { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* 状态图标 */}
          <div className="mb-6">
            {status === 'loading' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-full bg-blue-50 flex items-center justify-center"
              >
                <Spinner className="w-10 h-10 text-blue-600 animate-spin" />
              </motion.div>
            )}
            {status === 'success' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center"
              >
                <CheckCircle className="w-10 h-10 text-green-600" weight="fill" />
              </motion.div>
            )}
            {status === 'error' && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                className="w-20 h-20 mx-auto rounded-full bg-red-50 flex items-center justify-center"
              >
                <XCircle className="w-10 h-10 text-red-600" weight="fill" />
              </motion.div>
            )}
          </div>

          {/* 标题 */}
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">
            {status === 'loading' && t('auth.googleLogin')}
            {status === 'success' && t('auth.loginSuccess')}
            {status === 'error' && t('auth.loginFailed')}
          </h1>

          {/* 消息 */}
          <p className="text-zinc-500 mb-8">{message}</p>

          {/* 操作按钮 */}
          {status !== 'loading' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="flex gap-3 justify-center"
            >
              {status === 'error' && (
                <Button onClick={handleRetry} variant="secondary">
                  {t('auth.backToLogin')}
                </Button>
              )}
              {status === 'success' && (
                <Button onClick={handleGoHome} variant="primary">
                  {t('auth.goToHome')}
                </Button>
              )}
            </motion.div>
          )}
        </div>

        {/* 底部版权 */}
        <Footer />
      </motion.div>
    </div>
  );
}
